// apps/mcv-client/src/components/Layout/SideNav/HlxSideMenu.tsx
import { Show, SignOutButton, useUser } from '@clerk/react-router';
import {
  ArrowLeftStartOnRectangleIcon,
  ChevronDoubleLeftIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useLocation } from 'react-router';
import { UserContextV2 } from '~/app';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { useFeatureFlag } from '~/features/feature-flags/useFeatureFlag';
import { getInitials } from '~/features/utils';
import { paths } from '~/routing';
import { isProviderAllowedRoute, normalizeRoutePathname } from '~/routing/providerAllowedRoutes';
import { HlxMcvSwitchLink } from './HlxMcvSwitchLink';
import { NavEntry } from './NavEntry';
import { HLX_EMPLOYER_SIDEBAR_LINKS, HLX_PROVIDER_SIDEBAR_LINKS } from './hlxConstants';
import type { SideMenuProps } from './types';

const TAGS = CT.layout.navDrawer;

const MenuProfile = ({
  title,
  subtitle,
  detail,
  onCollapse,
}: {
  detail?: string;
  onCollapse?: () => void;
  subtitle: string;
  title: string;
}) => {
  const { user } = useUser();
  return (
    <div className="relative px-3 pt-4 pb-3 border-b border-primary-t-700" data-cy={TAGS.layout}>
      {onCollapse && (
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          className="absolute top-2 right-2 p-1.5 rounded-md text-grey-400 hover:text-grey-200 hover:bg-white/10 transition-colors"
        >
          <ChevronDoubleLeftIcon className="size-5" />
        </button>
      )}
      <div className="rounded-lg bg-white/[0.06] px-3 py-4">
        <div className="flex flex-col items-center text-center gap-2.5">
          {user?.hasImage ? (
            <img
              src={user.imageUrl}
              alt={title}
              className="w-[52px] h-[52px] rounded-full object-cover ring-2 ring-white/20 shadow-lg"
            />
          ) : (
            <div className="w-[52px] h-[52px] rounded-full bg-gradient-to-br from-primary-50 to-primary-300 flex items-center justify-center text-white text-base font-semibold ring-2 ring-white/20 shadow-lg">
              {getInitials(title) || '?'}
            </div>
          )}
          <div className="min-w-0 w-full">
            <p className="text-base font-semibold text-grey-100 truncate leading-snug">{title}</p>
            <p className="text-[13px] text-grey-400 mt-1 truncate">{subtitle}</p>
            {detail && (
              <p className="text-[13px] text-grey-400 mt-0.5 truncate" title={detail}>
                {detail}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileButton = () => {
  return (
    <NavEntry href={paths.hlx.settings} label="Profile" pageKey="settings" icon={UserCircleIcon} />
  );
};

export const HlxSideMenu = ({ keyPrefix, onCollapse }: SideMenuProps) => {
  const location = useLocation();
  const { user: clerkUser } = useUser();

  const marketRef = UserContextV2.useSelector((state) => state.context.marketRef);
  const vaultRef = UserContextV2.useSelector((state) => state.context.vaultRef);
  const isProvider = UserContextV2.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  const isImpersonating = UserContextV2.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const isAdmin = UserContextV2.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_ADMIN' })
  );
  const providerCalendarEnabled = useFeatureFlag('providerCalendar');

  const employerUser = useSelector(marketRef, (state) => state?.context?.employerUser);
  const provider = useSelector(vaultRef, (state) => state?.context?.provider);

  const onSetPage = (page: string) => {
    if (marketRef) {
      marketRef.send({ type: 'ON_SET_PAGE', payload: { page } });
    }
  };

  const normalizedPathname = normalizeRoutePathname(location.pathname);
  const isProviderHlxView =
    isProvider &&
    (normalizedPathname === paths.hlx.dashboard || isProviderAllowedRoute(normalizedPathname));
  const providerSections = HLX_PROVIDER_SIDEBAR_LINKS.map((section) => ({
    ...section,
    subMenu: section.subMenu.filter(
      (item) => item.href !== paths.hlx.calendar || providerCalendarEnabled
    ),
  })).filter((section) => section.subMenu.length > 0);
  const sections = isProviderHlxView ? providerSections : HLX_EMPLOYER_SIDEBAR_LINKS;

  const employerName =
    employerUser?.employer?.employerName || employerUser?.employer?.employerInfo?.legalEntityName;
  // Clerk is where the Profile page saves the name, so it is the live source; our stored copy
  // is only set at registration.
  const employerContact =
    [clerkUser?.firstName, clerkUser?.lastName].filter(Boolean).join(' ') ||
    [employerUser?.firstName, employerUser?.lastName].filter(Boolean).join(' ');

  const providerName = [provider?.firstName, provider?.lastName, provider?.suffixName]
    .filter(Boolean)
    .join(' ');

  if (!isProviderHlxView && !employerUser && !isAdmin) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-primary-t-900 rounded-lg w-72 flex flex-col h-full min-h-[25vh]">
      {isProviderHlxView ? (
        <MenuProfile
          title={providerName || 'HealthcareLX'}
          subtitle={provider?.type?.description || 'Browse active job listings'}
          detail={provider?.npiNum ? `NPI: ${provider.npiNum}` : undefined}
          onCollapse={onCollapse}
        />
      ) : (
        <MenuProfile
          title={employerName || 'HealthcareLX'}
          subtitle={employerContact || 'Employer workspace'}
          onCollapse={onCollapse}
          detail={clerkUser?.primaryEmailAddress?.emailAddress}
        />
      )}

      {isProviderHlxView && (
        <div className="border-b border-primary-t-700 py-2">
          <HlxMcvSwitchLink />
        </div>
      )}

      <div className="flex-1 overflow-y-auto py-1">
        {sections.map((section) => (
          <div
            key={`${keyPrefix}-${section.label}`}
            className="flex flex-col pb-2 border-b border-primary-t-700 mt-1"
          >
            <div className="flex justify-between items-center px-4 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-t-200">
                {section.label}
              </p>
            </div>
            {section.subMenu.map((item, idx) => (
              <NavEntry
                key={`${keyPrefix}-${item.pageKey}-${idx}`}
                href={item.href}
                label={item.label}
                pageKey={item.pageKey}
                icon={item.icon}
                onSetPage={onSetPage}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="border-t border-primary-t-700 px-2 py-2 flex flex-col gap-0.5">
        {/* [MCV-1752] While impersonating, Sign Out would end the employer's own session; they exit through the banner instead. */}
        {!isImpersonating && (
          <Show when="signed-in">
            <ProfileButton />
            <SignOutButton data-cy={CT.Homepage.signoutBtn} redirectUrl={paths.landing}>
              <div className="mx-2">
                <div className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-grey-200 hover:bg-white/50 transition-colors duration-150">
                  <ArrowLeftStartOnRectangleIcon className="size-4 text-grey-400" />
                  <span className="text-sm" data-cy="sign-out-button">
                    Sign Out
                  </span>
                </div>
              </div>
            </SignOutButton>
          </Show>
        )}
      </div>
    </div>
  );
};
